import React from 'react';
import { Link } from 'react-router-dom';
import DashboardCard, { DashboardLink, TABLE_HEAD_CLASS } from './DashboardCard';

const COLUMNS = [
  { key: 'apply', label: 'Apply', head: 'text-emerald-600' },
  { key: 'mayApply', label: 'May Apply', head: 'text-amber-600' },
  { key: 'neutral', label: 'Neutral', head: 'text-slate-500' },
  { key: 'avoid', label: 'Avoid', head: 'text-rose-600' },
];

// Shade each count by what it signals; zeros stay muted
const cellClass = (key, value, row) => {
  if (!value) return 'text-slate-300';
  if (key === 'apply') return value >= 10 ? 'bg-emerald-100 text-emerald-800 font-extrabold' : 'bg-emerald-50 text-emerald-700 font-bold';
  if (key === 'mayApply') return 'text-amber-700 font-bold';
  if (key === 'avoid') return 'bg-rose-50 text-rose-600 font-bold';
  // Neutral stands out only when it's the dominant call
  const dominant = value > row.apply && value > row.mayApply && value > row.avoid;
  return dominant ? 'bg-slate-100 text-slate-800 font-bold' : 'text-slate-600';
};

// rows: utils/ipoDashboard.js buildBrokerRatingRows
export const BrokerRatingsCard = ({ board, rows, loading, error, onRetry, query = '' }) => {
  const isSme = board.key === 'sme';
  const totalReviews = rows.reduce((sum, row) => sum + row.total, 0);
  return (
    <DashboardCard
      title="IPO Reviews & Broker Ratings"
      subtitle={`Consensus of broker research calls on current ${isSme ? 'SME' : 'mainboard'} IPOs`}
      aside={
        <span className="px-2 py-0.5 rounded-md border border-teal-200 bg-teal-50 text-[10px] font-bold text-[#006666] whitespace-nowrap">
          {isSme ? 'BSE SME & NSE Emerge' : 'BSE & NSE'}
        </span>
      }
      footer={
        <DashboardLink to={isSme ? '/sme-ipo-reviews' : '/ipo-reviews'}>
          {totalReviews ? `View All ${totalReviews} Broker Review${totalReviews === 1 ? '' : 's'}` : 'Browse All IPO Reviews'}
        </DashboardLink>
      }
    >
      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className={TABLE_HEAD_CLASS}>
              <th className="py-3 pl-4 sm:pl-5 pr-3 min-w-[170px]">Company</th>
              {COLUMNS.map((col) => (
                <th key={col.key} className={`py-3 px-2 text-center whitespace-nowrap ${col.head}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
            {loading &&
              Array.from({ length: 4 }).map((_, idx) => (
                <tr key={`skeleton-${idx}`} className="animate-pulse">
                  <td className="py-3 pl-4 sm:pl-5 pr-3"><div className="h-4 w-36 rounded bg-slate-200" /></td>
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="py-3 px-2"><div className="mx-auto h-6 w-8 rounded bg-slate-100" /></td>
                  ))}
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="py-10 px-4 text-center text-sm text-slate-500">
                  {error ? (
                    <>
                      Broker reviews are temporarily unavailable.{' '}
                      <button type="button" onClick={onRetry} className="font-bold text-[#008080] hover:underline">
                        Retry
                      </button>
                    </>
                  ) : query.trim() ? (
                    `No broker reviews for “${query.trim()}”.`
                  ) : (
                    `No broker has published a review of a current ${isSme ? 'SME ' : ''}IPO yet.`
                  )}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="py-2.5 pl-4 sm:pl-5 pr-3 font-semibold text-slate-800">
                    {row.link ? (
                      <Link to={row.link} className="hover:text-[#008080] hover:underline">
                        {row.company}
                      </Link>
                    ) : (
                      row.company
                    )}
                  </td>
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="py-1.5 px-2 text-center">
                      <span className={`inline-flex items-center justify-center min-w-[32px] px-2 py-1 rounded-md tabular-nums ${cellClass(col.key, row[col.key], row)}`}>
                        {row[col.key]}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </DashboardCard>
  );
};

export default BrokerRatingsCard;
